/**
 * JIRA CSV Parser
 * Parses JIRA CSV exports and extracts work items (Epics, Stories, Tasks)
 */

/**
 * Parse CSV text into rows
 */
function parseCSV(csvText) {
  const lines = [];
  let currentLine = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      currentLine.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // End of line
      if (currentField || currentLine.length > 0) {
        currentLine.push(currentField.trim());
        lines.push(currentLine);
        currentLine = [];
        currentField = '';
      }
      // Skip \r\n
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
    } else {
      currentField += char;
    }
  }
  
  // Add last field and line
  if (currentField || currentLine.length > 0) {
    currentLine.push(currentField.trim());
    lines.push(currentLine);
  }
  
  return lines;
}

/**
 * Map JIRA status to Peepz status
 */
function mapJiraStatusToPeepz(jiraStatus) {
  if (!jiraStatus) return 'TODO';
  
  const status = jiraStatus.toLowerCase().trim();
  
  if (status === 'to do' || status === 'todo' || status === 'open') {
    return 'TODO';
  }
  if (status === 'in progress' || status === 'inprogress' || status === 'in progress') {
    return 'IN_PROGRESS';
  }
  if (status === 'in review' || status === 'review' || status === 'code review') {
    return 'REVIEW';
  }
  if (status === 'done' || status === 'resolved' || status === 'closed') {
    return 'DONE';
  }
  
  return 'TODO'; // Default
}

/**
 * Parse JIRA CSV and extract work items
 */
export function parseJiraCSV(csvText) {
  const lines = parseCSV(csvText);
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }
  
  const headers = lines[0].map(h => h.toLowerCase().trim());
  const dataRows = lines.slice(1);
  
  // Find column indices
  const getColumnIndex = (possibleNames) => {
    for (const name of possibleNames) {
      const index = headers.findIndex(h => h.includes(name.toLowerCase()));
      if (index !== -1) return index;
    }
    return -1;
  };
  
  const issueTypeIdx = getColumnIndex(['issue type', 'issuetype']);
  const summaryIdx = getColumnIndex(['summary']);
  const descriptionIdx = getColumnIndex(['description']);
  const epicNameIdx = getColumnIndex(['epic name', 'custom field (epic name)']);
  const parentIdx = getColumnIndex(['parent', 'parent key']);
  const parentSummaryIdx = getColumnIndex(['parent summary']);
  const statusIdx = getColumnIndex(['status']);
  const storyPointsIdx = getColumnIndex(['story points', 'custom field (story points)', 'story point estimate']);
  const assigneeIdx = getColumnIndex(['assignee']);
  const issueKeyIdx = getColumnIndex(['issue key']);
  const issueIdIdx = getColumnIndex(['issue id']);
  
  if (issueTypeIdx === -1 || summaryIdx === -1) {
    throw new Error('CSV must contain "Issue Type" and "Summary" columns');
  }
  
  const epics = [];
  const stories = [];
  const tasks = [];
  const itemsById = new Map(); // Track items by issue key/id for parent relationships
  
  // First pass: collect all items
  dataRows.forEach((row, index) => {
    if (!row || !Array.isArray(row)) {
      console.warn(`Skipping invalid row at index ${index}`);
      return;
    }
    
    if (row.length < headers.length) {
      // Pad row if needed
      while (row.length < headers.length) {
        row.push('');
      }
    }
    
    const issueType = (row[issueTypeIdx] || '').toString().trim();
    const summary = (row[summaryIdx] || '').toString().trim();
    const description = (row[descriptionIdx] || '').toString().trim();
    const epicName = epicNameIdx !== -1 ? (row[epicNameIdx] || '').toString().trim() : '';
    const parent = parentIdx !== -1 ? (row[parentIdx] || '').toString().trim() : '';
    const parentSummary = parentSummaryIdx !== -1 ? (row[parentSummaryIdx] || '').toString().trim() : '';
    const status = statusIdx !== -1 ? (row[statusIdx] || '').toString().trim() : '';
    const storyPoints = storyPointsIdx !== -1 ? (row[storyPointsIdx] || '').toString().trim() : '';
    const assignee = assigneeIdx !== -1 ? (row[assigneeIdx] || '').toString().trim() : '';
    const issueKey = issueKeyIdx !== -1 ? (row[issueKeyIdx] || '').toString().trim() : `ITEM-${index}`;
    const issueId = issueIdIdx !== -1 ? (row[issueIdIdx] || '').toString().trim() : '';
    
    // Skip rows without summary
    if (!summary) {
      return;
    }
    
    const item = {
      issueKey,
      issueId,
      issueType,
      summary,
      description,
      epicName,
      parent,
      parentSummary,
      status: mapJiraStatusToPeepz(status),
      storyPoints: storyPoints ? parseFloat(storyPoints) : null,
      assignee,
      rowIndex: index + 2, // +2 for header and 1-based index
    };
    
    itemsById.set(issueKey, item);
    if (issueId) {
      itemsById.set(issueId, item);
    }
    
    // Determine item type
    const isEpic = issueType.toLowerCase() === 'epic' || epicName !== '';
    
    if (isEpic) {
      epics.push(item);
    } else if (issueType.toLowerCase() === 'story') {
      stories.push(item);
    } else if (issueType.toLowerCase() === 'task' || issueType.toLowerCase() === 'subtask') {
      tasks.push(item);
    } else {
      // Unknown type, treat as task
      tasks.push(item);
    }
  });
  
  // Second pass: build parent-child relationships
  const parentChildMap = new Map(); // epic -> [children]
  
  [...stories, ...tasks].forEach(item => {
    if (item.parent) {
      // Try to find parent by key or ID
      const parentItem = itemsById.get(item.parent) || 
                        Array.from(itemsById.values()).find(i => 
                          i.issueKey === item.parent || 
                          i.issueId === item.parent ||
                          i.summary === item.parentSummary
                        );
      
      if (parentItem) {
        const parentKey = parentItem.issueKey;
        if (!parentChildMap.has(parentKey)) {
          parentChildMap.set(parentKey, []);
        }
        parentChildMap.get(parentKey).push(item);
      }
    }
  });
  
  // Build epic children map
  epics.forEach(epic => {
    const children = parentChildMap.get(epic.issueKey) || [];
    epic.children = children;
  });
  
  return {
    epics,
    stories,
    tasks,
    itemsById,
    parentChildMap,
  };
}

